/**
 * Extension for each supported document type. Browsers disagree on the mime type
 * for some formats - Windows with Excel installed reports .csv as
 * application/vnd.ms-excel - so the extension is used as a fallback signal.
 * Images are matched on mime type alone, which browsers report reliably.
 */
const DOCUMENT_TYPES: Record<string, { extension: string; name: string }> = {
  'application/pdf': { extension: '.pdf', name: 'PDF' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extension: '.docx',
    name: 'Word',
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extension: '.xlsx',
    name: 'Excel',
  },
  'text/csv': { extension: '.csv', name: 'CSV' },
  'text/plain': { extension: '.txt', name: 'TXT' },
}

const MIME_TYPE_EXTENSIONS = Object.fromEntries(
  Object.entries(DOCUMENT_TYPES).map(([mime, { extension }]) => [mime, extension]),
)

const IMAGE_TYPE_NAMES: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
}

export const DEFAULT_UPLOAD_CONSTRAINTS = {
  /** Maximum file size: 50MB (aligned with main upload component) */
  maxSize: 50 * 1024 * 1024,
  /** Maximum number of files */
  maxFiles: 10,
  /**
   * Supported file types. Must stay in sync with the backend allow list in
   * services/internal/llm/file - anything else is rejected server-side with
   * UNSUPPORTED_TYPE, so accepting it here only defers the error.
   */
  supportedFileTypes: [...Object.keys(DOCUMENT_TYPES), ...Object.keys(IMAGE_TYPE_NAMES)],
}

export const SUPPORTED_FILE_TYPES_HINT = `${Object.values(DOCUMENT_TYPES)
  .map(({ name }) => name)
  .join(', ')}, and images (${Object.values(IMAGE_TYPE_NAMES).join(', ')})`

/**
 * Server-side compute (decrypt + write) tops out at ~3.3s platform-wide over the
 * last month, so the real bottleneck is client-side transfer time for large
 * files on slow connections, not measurable server-side; gateway logs show
 * successful uploads for this workload taking up to ~30-50s end-to-end. 300s is
 * a deliberate multiple of that observed worst case, not a tight bound.
 */
export const UPLOAD_ARTIFACT_TIMEOUT_MS = 300000

/**
 * Deliberately not wired to an `accept` attribute on the upload inputs:
 * rc-upload filters drag-dropped files against `accept` and discards them
 * silently, which would replace the explicit "type not supported" error with
 * no feedback at all.
 */
export const isSupportedFileType = (
  file: { name?: string; type?: string },
  allowedTypes: string[] = DEFAULT_UPLOAD_CONSTRAINTS.supportedFileTypes,
): boolean => {
  if (allowedTypes.includes(file.type || '')) {
    return true
  }

  const name = file.name?.toLowerCase() || ''
  return allowedTypes.some((type) => {
    const extension = MIME_TYPE_EXTENSIONS[type]
    return extension !== undefined && name.endsWith(extension)
  })
}
